/*
  Material tab pieces (UI overhaul wave 1, screen 05; wave 2 adds MaterialKpiStrip (idea 2) and
  MaterialRowHistory (idea 6) — the table itself is MaterialTable.jsx):
  - MaterialToolbar        — one navy "Add BOQ items" (+ ways to add), "New ▾" for the rest, Export (idea 1)
  - MaterialBulkBar        — appears when rows are selected; "Delete selected…" lives here now (idea 1)
  - MaterialProgressCell   — delivered ÷ (BOQ + VOs) bar + % (idea 4)
  - MaterialEmptyState     — three ways to start a BOQ instead of "No BOQ items" (idea 7)
  - MaterialConflictCallout — VOs / delivery notes that are not linked to any BOQ line (idea 7)
  Wave 3: MaterialVariationCallout — "Variation needed on 2 lines" + "Draft VO-005" (idea 8); "Delivery note from
  photo" in New ▾ (idea 10 — AI reads the photo; the dialog says "not set up" while the AI assistant is off).
*/
import {
  Camera, ChevronDown, ChevronRight, ClipboardPaste, Download, FileSpreadsheet, FilePlus2, GitCompareArrows, ListTodo, PackageCheck, PackageSearch,
  Plus, Search, Truck, X, ClipboardList, Trash2, TriangleAlert,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StatCard } from '@/components/ui/stat-card';
import { StatusPill } from '@/components/ui/status-pill';
import { RowActions } from '@/components/ui/row-actions';
import { formatDate, formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { ActionMenu, Callout, MONO, Panel, ProgressBar, TabHeader } from './ProjectUi';
import { formatCodeList, getMaterialProgress } from './projectDetailModel';
import { requestLabel } from './materialModel';

export function MaterialToolbar({
  subtitle,
  canEdit,
  canAddVariation = canEdit,
  canEditSiteNeeds = canEdit,
  canAddTask = true,
  hasBoq,
  canExport,
  onExport,
  onAddManually,
  onImportExcel,
  onPasteRows,
  onFromCatalog,
  onDownloadTemplate,
  onNewDeliveryNote,
  onNewSiteRequest,
  siteRequestHint,
  onNewVariation,
  onNewSiteNeeds,
  onNewTask,
  onDeliveryNoteFromPhoto,
}) {
  // Wave 2: no page-wide Edit mode any more — canEdit is the role's Edit level on Material (Admin › Access).
  // 28 #8: what a role may not change is not offered at all (hidden, not greyed): the Material writes need
  // canEdit; "Variation", "Site needs" and "Task" follow the module the server checks for them (Variations;
  // Projects + Material; Tasks). Nothing left to create → no "New" menu; no Material edit → no "Add BOQ items".
  const newItems = [
    canEdit && { label: 'Delivery note', icon: Truck, onSelect: onNewDeliveryNote, testId: 'material-new-delivery-note' },
    canEdit && onDeliveryNoteFromPhoto ? {
      label: 'DN from photo',
      icon: Camera,
      onSelect: onDeliveryNoteFromPhoto,
      disabled: !hasBoq,
      hint: !hasBoq ? 'Needs BOQ' : 'AI',
      description: 'Take a photo of the paper DN; check the lines, then save',
      testId: 'material-new-dn-photo',
    } : null,
    canEdit && {
      label: 'Site request',
      icon: PackageCheck,
      onSelect: onNewSiteRequest,
      disabled: !onNewSiteRequest,
      hint: siteRequestHint,
      testId: 'material-new-site-request',
    },
    canAddVariation && { label: 'Variation', icon: GitCompareArrows, onSelect: onNewVariation, disabled: !hasBoq, hint: !hasBoq ? 'Needs BOQ' : undefined, testId: 'material-new-variation' },
    canEditSiteNeeds && { label: 'Site needs', icon: ClipboardList, onSelect: onNewSiteNeeds, disabled: !hasBoq, hint: !hasBoq ? 'Needs BOQ' : undefined, testId: 'material-new-site-needs' },
  ].filter(Boolean);
  if (canAddTask && onNewTask) {
    newItems.push({ label: 'Task', icon: ListTodo, onSelect: onNewTask, separatorBefore: newItems.length > 0, testId: 'material-new-task' });
  }
  return (
    <TabHeader
      title="Material"
      subtitle={subtitle}
      actions={(
        <>
          <Button type="button" variant="ghost" onClick={onExport} disabled={!canExport} data-testid="material-export">
            <Download aria-hidden="true" />
            Export
          </Button>
          {newItems.length ? (
            <ActionMenu
              label="New"
              trigger={(
                <Button type="button" variant="outline" data-testid="material-new-menu">
                  <Plus aria-hidden="true" />
                  New
                  <ChevronDown aria-hidden="true" className="!size-3.5 text-ink-3" />
                </Button>
              )}
              items={newItems}
            />
          ) : null}
          {canEdit ? (
            <div className="inline-flex rounded-control shadow-[0_1px_2px_rgb(25_25_112/0.25)]">
              <Button
                type="button"
                onClick={onAddManually}
                className="rounded-r-none shadow-none"
                data-testid="material-add-boq"
              >
                <Plus aria-hidden="true" />
                Add BOQ items
              </Button>
              <ActionMenu
                label="Ways to add to the BOQ"
                trigger={(
                  <Button
                    type="button"
                    size="icon"
                    aria-label="More ways to fill the BOQ"
                    className="w-8 rounded-l-none border-l border-white/20 shadow-none"
                    data-testid="material-add-boq-menu"
                  >
                    <ChevronDown aria-hidden="true" />
                  </Button>
                )}
                items={[
                  { label: 'Add manually', icon: FilePlus2, onSelect: onAddManually, description: 'Search the catalog and set quantities' },
                  { label: 'Import Excel', icon: FileSpreadsheet, onSelect: onImportExcel, hint: '.xlsx .csv', description: 'Part No, Description, BOQ Qty columns' },
                  { label: 'Paste rows', icon: ClipboardPaste, onSelect: onPasteRows, description: 'Copy rows from Excel and paste them' },
                  { label: 'From catalog', icon: PackageSearch, onSelect: onFromCatalog, description: 'Pick inventory items one by one' },
                  { label: 'Download template', icon: Download, onSelect: onDownloadTemplate, separatorBefore: true },
                ]}
              />
            </div>
          ) : null}
        </>
      )}
    />
  );
}

export function MaterialBulkBar({ count, labels = [], canEdit, onDelete, onSiteRequest, onClear }) {
  if (!count) return null;
  return (
    <div
      role="region"
      aria-label="Selected BOQ lines"
      className="sticky bottom-4 z-20 mx-auto flex w-fit max-w-full flex-wrap items-center gap-2 rounded-card bg-[color:var(--brand)] px-3 py-2 text-[13px] text-[color:var(--brand-fg)] shadow-overlay"
      data-testid="material-bulk-bar"
    >
      <span className="px-1 font-semibold">{count} selected</span>
      {labels.length ? (
        <span className={cn('hidden max-w-[320px] truncate text-[12px] text-white/75 md:inline', MONO)}>{labels.slice(0, 4).join(', ')}{labels.length > 4 ? ` +${labels.length - 4}` : ''}</span>
      ) : null}
      {/* 28 #8: a View-only Material role selects lines to read them; it gets no Site request / Delete */}
      {onSiteRequest && canEdit ? (
        <Button type="button" size="sm" variant="ghost" onClick={onSiteRequest} className="text-white hover:bg-white/10 hover:text-white">
          <PackageCheck aria-hidden="true" />
          Site request
        </Button>
      ) : null}
      {canEdit ? (
        <Button type="button" size="sm" variant="ghost" onClick={onDelete} className="text-white hover:bg-white/10 hover:text-white" data-testid="material-bulk-delete">
          <Trash2 aria-hidden="true" />
          Delete selected…
        </Button>
      ) : null}
      <Button type="button" size="icon-sm" variant="ghost" onClick={onClear} aria-label="Clear selection" className="text-white hover:bg-white/10 hover:text-white">
        <X aria-hidden="true" />
      </Button>
    </div>
  );
}

/** tight: the narrow version at every width (Sub BOQ tables); otherwise it widens from a 1366px window. */
export function MaterialProgressCell({ row, testId, compact = false, tight = false }) {
  const progress = getMaterialProgress(row);
  return (
    <div className={cn('flex items-center gap-2 min-w-[80px]', !tight && (compact ? 'min-[1366px]:min-w-[96px]' : 'min-[1366px]:min-w-[118px]'))} data-testid={testId} title={`${progress.delivered} of ${progress.planned} delivered`}>
      <ProgressBar value={progress.shown} tone={progress.tone} className={cn('w-[40px]', !tight && (compact ? 'min-[1366px]:w-[52px]' : 'min-[1366px]:w-[64px]'))} label="Delivered" />
      <span className={cn('text-[12px] font-semibold tabular-nums', progress.over ? 'text-status-amber-ink' : 'text-ink-2')}>
        {progress.percent}%
      </span>
    </div>
  );
}

export function MaterialEmptyState({ canEdit, onImportExcel, onDownloadTemplate, onPasteRows, onFromCatalog }) {
  const ways = [
    {
      key: 'excel',
      icon: FileSpreadsheet,
      title: 'Import Excel',
      text: 'Upload the BOQ sheet (.xlsx or .csv) with Part No, Description and BOQ Qty columns.',
      action: (
        <div className="flex flex-wrap items-center gap-2">
          {canEdit ? <Button type="button" size="sm" variant="outline" onClick={onImportExcel} data-testid="material-empty-import">Choose file</Button> : null}
          <Button type="button" size="sm" variant="link" className="h-auto px-0" onClick={onDownloadTemplate}>Download template</Button>
        </div>
      ),
    },
    {
      key: 'paste',
      icon: ClipboardPaste,
      title: 'Paste from Excel',
      text: 'Copy the rows in Excel and paste them; quantities of the same part are added up.',
      action: canEdit ? <Button type="button" size="sm" variant="outline" onClick={onPasteRows}>Paste rows</Button> : null,
    },
    {
      key: 'catalog',
      icon: Search,
      title: 'Pick from catalog',
      text: 'Search the inventory catalog and tick the items this project needs.',
      action: canEdit ? <Button type="button" size="sm" variant="outline" onClick={onFromCatalog}>Open catalog</Button> : null,
    },
  ];
  return (
    <Panel className="p-5 sm:p-6" data-testid="material-empty-state">
      <h3 className="font-heading text-[16px] font-extrabold text-ink">This project has no BOQ yet</h3>
      <p className="mt-1 max-w-[680px] text-[13px] text-ink-3">
        Start with the BOQ: deliveries, site requests and variations are all counted against its lines.
        {canEdit ? '' : ' Your role can view the BOQ only.'}
      </p>
      <div className="mt-4 grid gap-3 md:grid-cols-3">
        {ways.map((way) => {
          const Icon = way.icon;
          return (
            <div key={way.key} className="flex flex-col gap-3 rounded-[12px] border border-line bg-surface-2 p-4">
              <span className="ui-icon-tile tone-navy" aria-hidden="true"><Icon className="h-[18px] w-[18px]" /></span>
              <div>
                <div className="text-[14px] font-bold text-ink">{way.title}</div>
                <p className="mt-1 text-[12.5px] leading-snug text-ink-3">{way.text}</p>
              </div>
              <div className="mt-auto">{way.action}</div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

export function MaterialConflictCallout({ conflict, hasBoq, onOpenDeliveryNote, onOpenVariations }) {
  if (!conflict?.any) return null;
  const codes = [...conflict.variations, ...conflict.deliveryNotes.map((note) => note.number)];
  const noteNumbers = conflict.deliveryNotes.map((note) => note.number);
  const notesText = noteNumbers.length > 3
    ? `${noteNumbers.length} delivery notes (${noteNumbers[0]}…${noteNumbers[noteNumbers.length - 1]})`
    : formatCodeList(noteNumbers);
  const subjectParts = [formatCodeList(conflict.variations), notesText].filter(Boolean);
  const plural = codes.length > 1;
  return (
    <Callout
      tone="amber"
      icon={TriangleAlert}
      data-testid="material-conflict"
      actions={(
        <>
          {conflict.variations.length && onOpenVariations ? (
            <Button type="button" size="sm" variant="outline" onClick={onOpenVariations}>Open Variations</Button>
          ) : null}
          {conflict.deliveryNotes.slice(0, 2).map((note) => (
            <Button key={note.id} type="button" size="sm" variant="outline" onClick={() => onOpenDeliveryNote?.(note.id)}>
              Open <span className={MONO}>{note.number}</span>
            </Button>
          ))}
        </>
      )}
    >
      <b className="font-semibold text-ink">
        {subjectParts.map((part, index) => (
          <span key={part}>
            {index > 0 ? ' and ' : ''}
            <span className={noteNumbers.length > 3 && index === subjectParts.length - 1 && conflict.deliveryNotes.length ? undefined : MONO}>{part}</span>
          </span>
        ))}
        {` ${plural ? 'are' : 'is'} not linked to ${hasBoq ? 'a matching' : 'any'} BOQ line`}
      </b>
      {' — '}
      {hasBoq
        ? `${plural ? 'their' : 'its'} quantities don't count toward Delivered or Remaining until the part numbers match a BOQ line.`
        : `add the BOQ first so ${plural ? 'these quantities count' : 'its quantities count'} toward Delivered, Remaining and Variations.`}
    </Callout>
  );
}

/* ---------------------------------------------------------------- wave 3 (screen 05 idea 8) */

/**
 * "Variation needed on 2 lines — site needs exceed BOQ + VOs: BE-GT20W.02 (+6), SCN-P360D4.01 (+4)" with
 * "Draft VO-005", which opens Add variation with these lines and quantities filled in (05 #8).
 */
export function MaterialVariationCallout({ summary, nextVoNumber, onDraft, canDraft = true }) {
  if (!summary?.count) return null;
  return (
    <Callout
      tone="violet"
      icon={GitCompareArrows}
      data-testid="material-vo-needed"
      actions={onDraft ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onDraft}
          disabled={!canDraft}
          title={canDraft ? `Opens Add variation with ${summary.count} line${summary.count === 1 ? '' : 's'} filled in` : 'Your role cannot add variations'}
          data-testid="material-draft-vo"
        >
          <GitCompareArrows aria-hidden="true" />
          Draft <span className={MONO}>{nextVoNumber || 'VO'}</span>
        </Button>
      ) : null}
    >
      <b className="font-semibold text-ink">Variation needed on {summary.count} line{summary.count === 1 ? '' : 's'}</b>
      {' — site needs exceed BOQ + VOs: '}
      {summary.lines.slice(0, 4).map((line, index) => (
        <span key={line.id || line.part}>
          {index > 0 ? ', ' : ''}
          <span className={cn(MONO, 'font-semibold text-ink')}>{line.part}</span>
          {' '}
          <span className="font-semibold text-status-red-ink">(+{formatNumber(line.needed, { maxDecimals: 2 })})</span>
        </span>
      ))}
      {summary.count > 4 ? ` +${summary.count - 4} more` : ''}
      .
    </Callout>
  );
}

/* ---------------------------------------------------------------- wave 2 (screen 05 ideas 2 and 6) */

/** Five numbers above the table, all from the loaded BOQ / VOs / delivery notes / site requests (05 #2). */
export function MaterialKpiStrip({ kpis }) {
  if (!kpis) return null;
  const pcs = (value) => formatNumber(value, { maxDecimals: 2 });
  const shownNotes = kpis.deliveryNotes.slice(0, 2).join(', ') + (kpis.deliveryNotes.length > 2 ? ` +${kpis.deliveryNotes.length - 2}` : '');
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-testid="material-kpis">
      <StatCard
        label="BOQ lines"
        value={formatNumber(kpis.lines, { maxDecimals: 0 })}
        meta={[
          kpis.parts !== kpis.lines ? `${kpis.parts} parts` : null,
          kpis.voExtra ? `${kpis.voExtra > 0 ? '+' : ''}${pcs(kpis.voExtra)} pcs from ${kpis.voCount} VO${kpis.voCount === 1 ? '' : 's'}` : 'No variation quantities',
        ].filter(Boolean).join(' · ')}
      />
      <StatCard
        label="Delivered"
        value={`${kpis.deliveredPercent}%`}
        progress={Math.min(100, kpis.deliveredPercent)}
        progressTone={kpis.deliveredPercent >= 100 ? 'green' : 'lime'}
        progressLabel="Delivered of BOQ + VOs"
        meta={`${pcs(kpis.delivered)} of ${pcs(kpis.planned)} pcs${shownNotes ? ` · ${shownNotes}` : ''}`}
      />
      <StatCard
        label="Remaining"
        value={pcs(kpis.remaining)}
        unit="pcs"
        valueTone={kpis.remaining > 0 ? 'amber' : undefined}
        meta={kpis.shortLines ? `${kpis.shortLines} line${kpis.shortLines === 1 ? '' : 's'} short` : 'Everything delivered'}
      />
      <StatCard
        label="Variation needed"
        value={formatNumber(kpis.voNeeded, { maxDecimals: 0 })}
        unit={kpis.voNeeded === 1 ? 'line' : 'lines'}
        valueTone={kpis.voNeeded ? 'red' : undefined}
        meta={kpis.voNeeded ? <span className={MONO}>{kpis.voNeededCodes.slice(0, 2).join(', ')}{kpis.voNeeded > 2 ? ` +${kpis.voNeeded - 2}` : ''}</span> : 'Site needs fit BOQ + VOs'}
      />
      <StatCard
        label="Open site requests"
        value={formatNumber(kpis.openRequests, { maxDecimals: 0 })}
        meta={kpis.openRequests ? `${pcs(kpis.openRequestQty)} pcs${kpis.nextDue ? ` · next due ${formatDate(kpis.nextDue)}` : ''}` : 'None open'}
      />
    </div>
  );
}

/**
 * Totals under the BOQ table — the SAME numbers as the KPI strip (one basis for the whole tab). On Sub BOQ
 * projects the rows above are per one Sub BOQ unit, so the totals say they include every Sub BOQ × its qty.
 */
export function MaterialTotalsFooter({ kpis, lineCount, subBoq = false }) {
  if (!kpis) return null;
  const pcs = (value) => formatNumber(value, { maxDecimals: 2 });
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-line bg-surface-2 px-4 py-2.5 text-[12.5px] text-ink-3 sm:px-5"
      data-testid="material-totals"
    >
      <span>
        Totals · {lineCount} line{lineCount === 1 ? '' : 's'}
        {subBoq ? <span data-testid="material-totals-basis"> · every Sub BOQ × its qty (same as the Main BOQ summary)</span> : null}
      </span>
      <span className="tabular-nums">
        BOQ <b className="font-semibold text-ink">{pcs(kpis.boq)}</b>
        {' · '}VOs <b className="font-semibold text-ink">{kpis.voExtra > 0 ? '+' : ''}{pcs(kpis.voExtra)}</b>
        {' · '}Delivered <b className="font-semibold text-ink">{pcs(kpis.delivered)}</b>
        {' · '}Remaining <b className="font-semibold text-ink">{pcs(kpis.remaining)}</b>
      </span>
    </div>
  );
}

/** "Full history" at the end of the opened row: the part traced from request to site (same as ⋯ › Where is it?). */
function FullHistoryLink({ onOpenTrace }) {
  if (!onOpenTrace) return null;
  return (
    <Button
      type="button"
      variant="link"
      size="sm"
      onClick={onOpenTrace}
      className="ml-auto h-auto px-0 text-[12.5px]"
      data-testid="material-history-full"
    >
      Full history
      <ChevronRight aria-hidden="true" className="!size-3.5" />
    </Button>
  );
}

/** The opened row (05 #6): which VOs added to the line, deliveries, site requests and site needs. */
export function MaterialRowHistory({ history, onOpenDeliveryNote, onDeleteRequest, canEdit, onOpenTrace }) {
  if (history.empty) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-[12.5px] text-ink-3">No variations, deliveries or site requests for this line yet.</p>
        <FullHistoryLink onOpenTrace={onOpenTrace} />
      </div>
    );
  }
  const qty = (value) => formatNumber(value, { maxDecimals: 2 });
  return (
    <div className="flex flex-wrap items-center gap-2" aria-label="Line history">
      <span className="mr-1 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">History</span>
      {history.variations.map((entry) => (
        <StatusPill key={entry.id} tone="violet" size="sm" label={`${entry.code} ${entry.qty > 0 ? '+' : ''}${qty(entry.qty)}`} />
      ))}
      {history.deliveries.map((entry) => (
        <button
          key={entry.id}
          type="button"
          onClick={() => onOpenDeliveryNote?.(entry.noteId)}
          className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          title={`Open delivery note ${entry.code}`}
        >
          <StatusPill tone="navy" size="sm" label={`${entry.code} · ${qty(entry.qty)} pcs${entry.date ? ` · ${formatDate(entry.date, { year: 'auto' })}` : ''}`} />
        </button>
      ))}
      {history.requests.map((request) => (
        <span key={request.id} className="inline-flex items-center gap-0.5">
          <StatusPill tone="amber" size="sm" label={requestLabel(request)} title={request.notes || undefined} />
          {canEdit && onDeleteRequest ? (
            <RowActions
              size="sm"
              label={`Actions for site request due ${request.due ? formatDate(request.due) : ''}`}
              items={[{ label: 'Delete site request…', icon: Trash2, danger: true, onSelect: () => onDeleteRequest(request) }]}
            />
          ) : null}
        </span>
      ))}
      {history.needs ? (
        <StatusPill
          tone={history.needs.extra > 0 ? 'red' : 'gray'}
          size="sm"
          label={`Site needs ${qty(history.needs.needs)} · BOQ + VOs ${qty(history.needs.planned)}${history.needs.extra > 0 ? ` → +${qty(history.needs.extra)}` : ''}`}
        />
      ) : null}
      <FullHistoryLink onOpenTrace={onOpenTrace} />
    </div>
  );
}
